import React from 'react';
import { IntercomConversation } from '../../types/intercom';

interface ConversationsListProps {
  conversations: IntercomConversation[];
  selectedConversation: IntercomConversation | null;
  onConversationSelect: (conversation: IntercomConversation) => void;
  loading: boolean;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  selectedConversation,
  onConversationSelect,
  loading
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      case 'snoozed':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'priority') {
      return (
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Conversations</h2>
        </div>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">
          Conversations ({conversations.length})
        </h2>
      </div>
      
      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No conversations found</h3>
            <p className="text-sm text-gray-600">
              Try adjusting your filters or sync conversations from Intercom.
            </p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => onConversationSelect(conversation)}
              className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedConversation?.id === conversation.id ? 'bg-indigo-50 border-r-2 border-indigo-600' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {getPriorityIcon(conversation.priority || '')}
                  <span className={`px-2 py-1 text-xs rounded-full ${getStateColor(conversation.state || 'unknown')}`}>
                    {conversation.state || 'unknown'}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {conversation.updated_at_intercom ? formatDate(conversation.updated_at_intercom) : 'Unknown'}
                </span>
              </div>

              <div className="mb-2">
                {conversation.lead ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {conversation.lead.name}
                    </h4>
                    {conversation.lead.email && (
                      <p className="text-xs text-gray-600 truncate">
                        {conversation.lead.email}
                      </p>
                    )}
                  </div>
                ) : conversation.contact ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {conversation.contact.name || conversation.contact.email || 'Website Visitor'}
                    </h4>
                    {conversation.contact.email && (
                      <p className="text-xs text-gray-600 truncate">
                        {conversation.contact.email}
                      </p>
                    )}
                  </div>
                ) : (
                  <h4 className="text-sm font-medium text-gray-900">
                    Website Visitor
                  </h4>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{conversation.conversation_message_count || 0} messages</span>
                {conversation.assignee_name && (
                  <span className="truncate ml-2">
                    Assigned to {conversation.assignee_name}
                  </span>
                )}
              </div>

              {conversation.tags && conversation.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {conversation.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {conversation.tags.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      +{conversation.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationsList;